import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Check, X, CircleSlash, Send, ShieldCheck, FileDown, Camera, Star, AlignLeft, ListChecks, Save, Trash2 } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";
import DistributionDialog from "@/components/DistributionDialog";
import CloseOutDialog from "@/components/CloseOutDialog";
import { formatDateTime } from "@/lib/format";

// Helpers -------------------------------------------------------------
const isAnswered = (it) => {
  const kind = it.kind || "passfail";
  if (kind === "passfail") return ["pass", "fail", "na"].includes(it.status);
  if (kind === "written") return !!(it.answer_text && it.answer_text.trim());
  if (kind === "multiple_choice") return (it.answer_choices || []).length > 0;
  if (kind === "rank") return it.answer_rank != null && it.answer_rank > 0;
  return false;
};
const photoOk = (it) => !it.photo_required || (it.photo_ids || []).length > 0;
const requiredOk = (it) => !it.required || isAnswered(it);
const failNoteOk = (it) => !((it.kind || "passfail") === "passfail" && it.status === "fail") || !!(it.note || "").trim();
const itemReady = (it) => isAnswered(it) && photoOk(it) && requiredOk(it) && failNoteOk(it);

// Save-and-resume drafts (localStorage-only, per-run) -------------------
// Fields kept as local state that live in the DOM before onBlur commits:
// - note, answer_text (Written), action_title, action_details, action_due_date
// If a supervisor closes the tab mid-audit, these unsaved buffers survive.
const draftKey = (runId) => `hubcmd:checklistRun:${runId}:drafts`;
const loadDrafts = (runId) => {
  try { return JSON.parse(localStorage.getItem(draftKey(runId)) || "{}") || {}; }
  catch { return {}; }
};
const saveDrafts = (runId, drafts) => {
  try {
    if (!drafts || Object.keys(drafts).length === 0) localStorage.removeItem(draftKey(runId));
    else localStorage.setItem(draftKey(runId), JSON.stringify(drafts));
  } catch (_) { /* quota — ignore */ }
};
const clearDraftField = (runId, idx, field) => {
  const d = loadDrafts(runId);
  if (d[idx]) {
    delete d[idx][field];
    if (Object.keys(d[idx]).length === 0) delete d[idx];
    saveDrafts(runId, d);
  }
};
const setDraftField = (runId, idx, field, value) => {
  const d = loadDrafts(runId);
  d[idx] = d[idx] || {};
  d[idx][field] = value;
  saveDrafts(runId, d);
};

const KIND_META = {
  passfail: { label: "Pass / Fail", icon: Check },
  written: { label: "Written", icon: AlignLeft },
  multiple_choice: { label: "Multiple choice", icon: ListChecks },
  rank: { label: "Likert", icon: Star },
};

export default function ChecklistRun() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [distOpen, setDistOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [draftBump, setDraftBump] = useState(0); // triggers re-check of banner
  const [resetBump, setResetBump] = useState(0); // bumped only on discard — forces ItemCard remount
  // pending drafts (only fields not yet committed to server)
  const pendingDrafts = useMemo(() => {
    if (!run) return {};
    const raw = loadDrafts(id);
    const out = {};
    Object.entries(raw).forEach(([idx, fields]) => {
      const it = run.items[Number(idx)];
      if (!it) return;
      const diff = {};
      for (const [k, v] of Object.entries(fields || {})) {
        const serverVal = it[k] ?? "";
        if ((v ?? "") !== (serverVal ?? "")) diff[k] = v;
      }
      if (Object.keys(diff).length > 0) out[idx] = diff;
    });
    return out;
  }, [id, run, draftBump]);
  const pendingCount = Object.keys(pendingDrafts).length;

  const load = async () => {
    const r = await api.get(`/checklist-runs/${id}`);
    setRun(r.data);
  };
  useEffect(() => {
    let alive = true;
    api.get(`/checklist-runs/${id}`).then((r) => { if (alive) setRun(r.data); });
    return () => { alive = false; };
  }, [id]);

  if (!run) return <div className="p-10 font-mono text-[#1a1a1a]">Loading…</div>;

  const setItem = async (idx, patch) => {
    const cur = run.items[idx];
    const merged = { ...cur, ...patch };
    const r = await api.patch(`/checklist-runs/${id}/item`, {
      item_index: idx,
      status: merged.status,
      note: merged.note,
      photo_ids: merged.photo_ids,
      answer_text: merged.answer_text,
      answer_choices: merged.answer_choices,
      answer_rank: merged.answer_rank,
      trigger_action: merged.trigger_action,
      action_details: merged.action_details,
      action_assignee_user_ids: merged.action_assignee_user_ids,
      action_assignee_team_ids: merged.action_assignee_team_ids,
      action_tags: merged.action_tags,
      action_due_date: merged.action_due_date,
    });
    // On successful commit, clear any drafts for fields we just synced
    Object.keys(patch || {}).forEach((k) => clearDraftField(id, idx, k));
    setDraftBump((n) => n + 1);
    setRun({ ...run, items: r.data.items });
  };

  const discardDrafts = () => {
    if (!window.confirm("Discard all unsaved local drafts for this audit?")) return;
    saveDrafts(id, {});
    setDraftBump((n) => n + 1);
    setResetBump((n) => n + 1); // force remount so buffered fields revert to server values immediately
  };

  // Counters
  const total = run.items.length;
  const ready = run.items.filter(itemReady).length;
  const blockers = run.items
    .map((it, i) => ({ it, i }))
    .filter(({ it }) => !itemReady(it));
  const fails = run.items.filter((it) => (it.kind || "passfail") === "passfail" && it.status === "fail").length;
  const passes = run.items.filter((it) => (it.kind || "passfail") === "passfail" && it.status === "pass").length;
  const completed = blockers.length === 0;
  const isClosed = run.status === "closed";

  const downloadPdf = (fileId) => {
    const token = localStorage.getItem("opscmd_token");
    window.open(`${API}/files/${fileId}?auth=${encodeURIComponent(token || "")}`, "_blank");
  };

  const onClosed = (data) => {
    saveDrafts(id, {}); // audit closed — nothing to resume anymore
    const pdfId = data?.signed_pdf_file_id;
    if (pdfId) setTimeout(() => downloadPdf(pdfId), 200);
    load();
  };

  return (
    <div className="p-6 lg:p-8 max-w-3xl mx-auto">
      <div className="bg-white border border-[#1a1a1a]/15 p-5 rounded-2xl">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Audit run · {run.display_id || ""}</div>
            <h2 className="font-display font-black text-2xl tracking-tight" data-testid="run-template-name">{run.template_name}</h2>
            <div className="text-[11px] font-mono text-[#1a1a1a]/60 uppercase tracking-wider mt-1">
              {run.status} · {formatDateTime(run.started_at)}
              {run.closed_by && <span> · closed by {run.closed_by}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {run.signed_pdf_file_id && (
              <button data-testid="open-signed-run-pdf" onClick={() => downloadPdf(run.signed_pdf_file_id)} className="btn-pill btn-pill-secondary">
                <FileDown className="w-3.5 h-3.5" /> Signed PDF
              </button>
            )}
            <a data-testid="run-open-trace" href={`/app/trace/run/${id}`} className="btn-pill btn-pill-secondary">
              <ShieldCheck className="w-3.5 h-3.5" /> Trace
            </a>
            <button data-testid="distribute-button" onClick={() => setDistOpen(true)}
              className="btn-pill btn-pill-secondary">
              <Send className="w-3.5 h-3.5" /> Quick distribute
            </button>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-px bg-[#1a1a1a]/15 border border-[#1a1a1a]/15 rounded-xl overflow-hidden">
          <Tile label="Ready" value={`${ready}/${total}`} color="#1a8754" />
          <Tile label="Fails (P/F)" value={fails} color="#ff4500" />
          <Tile label="Passes (P/F)" value={passes} color="#1a1a1a" />
        </div>
        {run.integrity_hash && (
          <div className="mt-3 inline-flex items-center gap-2 bg-[#fff1ea] border border-[#ff4500]/15 rounded-full px-3 py-1">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65">Tamper-evident · SHA-256</span>
            <span className="font-mono text-[10px] text-[#1a1a1a] truncate max-w-[280px]">{run.integrity_hash}</span>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-3">
        {!isClosed && pendingCount > 0 && (
          <div className="bg-[#fffbe6] border-2 border-[#d6a900]/40 rounded-2xl p-4 flex items-start justify-between gap-3" data-testid="resume-drafts-banner">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#a37e00] font-bold">// Resumed session</div>
              <div className="font-display font-black text-base text-[#1a1a1a]">You have unsaved local drafts on {pendingCount} question{pendingCount === 1 ? "" : "s"}.</div>
              <p className="text-[12px] text-[#1a1a1a]/70 mt-0.5">
                We restored the text you typed but didn&apos;t yet finish. Click into a field and press <span className="font-mono font-bold">Tab</span> or click away to save it to the server.
              </p>
            </div>
            <button data-testid="discard-drafts-btn" onClick={discardDrafts}
              className="btn-pill btn-pill-secondary flex-shrink-0">
              <Trash2 className="w-3.5 h-3.5" /> Discard drafts
            </button>
          </div>
        )}
        {run.items.map((it, i) => (
          <ItemCard key={`${i}-${resetBump}`} idx={i} item={it} runId={id}
            draft={pendingDrafts[i] || {}}
            onUpdate={(patch) => setItem(i, patch)}
            onDraftChange={() => setDraftBump((n) => n + 1)}
            readOnly={isClosed} />
        ))}
      </div>

      {!isClosed && (
        <div className="surface-card rounded-2xl p-5 mt-5 border-2 border-[#ff4500]/30 bg-gradient-to-br from-[#fff1ea] to-white" data-testid="closeout-cta-run">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Final step</div>
              <div className="font-display font-black text-xl">Ready to close this audit?</div>
              <p className="text-[12px] text-[#1a1a1a]/70 mt-1 max-w-xl">
                Closing out locks the record, generates a tamper-evident signed PDF, and sends it to the destinations your admin has configured (email · Teams · SharePoint).
              </p>
              {!completed && (
                <div className="mt-2 text-[11px] font-mono uppercase tracking-wider font-bold text-[#ff4500]" data-testid="run-blockers">
                  ⚠ {blockers.length} item(s) not ready — answer required questions and attach photos where flagged before closing.
                </div>
              )}
            </div>
            <button data-testid="closeout-bottom-run" onClick={() => setCloseOpen(true)} disabled={!completed}
              className="btn-pill bg-[#ff4500] text-white hover:bg-[#cc3500] shadow-glow font-display font-black text-base px-6 py-3 self-start md:self-auto whitespace-nowrap disabled:opacity-50">
              <ShieldCheck className="w-4 h-4" /> Close out audit & submit report
            </button>
          </div>
        </div>
      )}

      <DistributionDialog open={distOpen} onClose={() => setDistOpen(false)} run={run} />
      <CloseOutDialog open={closeOpen} onClose={() => setCloseOpen(false)}
        kind="audit" title={run.template_name}
        endpoint={`/checklist-runs/${id}/close-out`}
        onClosed={onClosed} />
    </div>
  );
}

function Tile({ label, value, color }) {
  return (
    <div className="bg-white p-3 text-center">
      <div className="font-display font-black text-2xl" style={{ color }}>{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/60 mt-1">{label}</div>
    </div>
  );
}

function ItemCard({ idx, item, onUpdate, readOnly, runId, draft, onDraftChange }) {
  const kind = item.kind || "passfail";
  const KindIcon = (KIND_META[kind] || KIND_META.passfail).icon;
  // Local `note` buffer — hydrated from server, then draft override if present.
  const [note, setNote] = useState(draft?.note ?? item.note ?? "");
  useEffect(() => { setNote(draft?.note ?? item.note ?? ""); }, [item.note]);
  const onNoteChange = (v) => {
    setNote(v);
    // stash to draft on every keystroke — survives tab close / accidental refresh
    if ((v ?? "") !== (item.note ?? "")) setDraftField(runId, idx, "note", v);
    else clearDraftField(runId, idx, "note");
    onDraftChange && onDraftChange();
  };
  const saveNote = () => { if (note !== item.note) onUpdate({ note }); };
  const hasNoteDraft = "note" in (draft || {});

  const needsPhoto = item.photo_required && !(item.photo_ids || []).length;
  const answered = isAnswered(item);
  const requiredUnmet = item.required && !answered;
  const flagged = (kind === "passfail" && item.status === "fail") || requiredUnmet || needsPhoto;

  return (
    <div className={`bg-white border ${flagged ? "border-[#ff4500]" : "border-[#1a1a1a]/15"} p-4 rounded-xl`} data-testid={`run-item-${idx}`}>
      <div className="flex items-start gap-3">
        <div className="font-mono text-[10px] text-[#ff4500] mt-1">{String(idx + 1).padStart(2, "0")}</div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
            <KindIcon className="w-3.5 h-3.5 text-[#1a1a1a]/55" />
            <span>{item.title}</span>
            {item.required && <span className="bg-[#ff4500] text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider rounded">Req</span>}
            {item.photo_required && <span className="bg-[#1a1a1a] text-white text-[9px] px-1.5 py-0.5 uppercase tracking-wider rounded inline-flex items-center gap-0.5"><Camera className="w-2.5 h-2.5" /> Photo</span>}
            <span className="ml-auto text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{(KIND_META[kind] || KIND_META.passfail).label}</span>
          </div>
          {item.instructions && <div className="text-[12px] text-[#1a1a1a]/70 mt-1">{item.instructions}</div>}

          {(item.example_photo_ids || []).length > 0 && (
            <div className="mt-2 bg-[#faf8f4] border border-[#1a1a1a]/06 rounded-md p-2" data-testid={`item-${idx}-example-photos`}>
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Reference · what to look for</div>
              <div className="flex flex-wrap gap-1.5">
                {(item.example_photo_ids || []).map((pid) => (
                  <a key={pid} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}?auth=${encodeURIComponent(localStorage.getItem("opscmd_token") || "")}`} target="_blank" rel="noreferrer">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${pid}?auth=${encodeURIComponent(localStorage.getItem("opscmd_token") || "")}`}
                         alt="" className="w-16 h-16 object-cover rounded border border-[#1a1a1a]/15 hover:border-[#ff4500]" />
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3">
            {kind === "passfail" && <PassFail idx={idx} item={item} onUpdate={onUpdate} readOnly={readOnly} />}
            {kind === "written" && <Written idx={idx} item={item} onUpdate={onUpdate} readOnly={readOnly} runId={runId} draft={draft} onDraftChange={onDraftChange} />}
            {kind === "multiple_choice" && <MultipleChoice idx={idx} item={item} onUpdate={onUpdate} readOnly={readOnly} />}
            {kind === "rank" && <Rank idx={idx} item={item} onUpdate={onUpdate} readOnly={readOnly} />}
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/60 font-mono">
                Note {kind === "passfail" && item.status === "fail" && <span className="text-[#ff4500] font-bold">· REQUIRED for FAIL</span>}
              </label>
              <div className="flex items-center gap-2">
                {hasNoteDraft && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[#a37e00] font-bold inline-flex items-center gap-1" data-testid={`item-${idx}-note-draft-badge`}>
                    <Save className="w-2.5 h-2.5" /> draft
                  </span>
                )}
                {kind === "passfail" && item.status === "fail" && !(note || "").trim() && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-[#ff4500] font-bold" data-testid={`item-${idx}-fail-note-missing`}>Explanation missing</span>
                )}
              </div>
            </div>
            <textarea data-testid={`item-${idx}-note`} value={note} disabled={readOnly} onChange={(e) => onNoteChange(e.target.value)} onBlur={saveNote}
              placeholder={kind === "passfail" && item.status === "fail" ? "REQUIRED — why did this fail? What did you observe?" : ""}
              className={`w-full bg-white border ${kind === "passfail" && item.status === "fail" && !(note || "").trim() ? "border-[#ff4500]" : "border-[#1a1a1a]/30"} px-2 py-1.5 text-sm h-16 rounded-md`} />
          </div>
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/60 font-mono">Photos {item.photo_required && <span className="text-[#ff4500]">· required</span>}</label>
              {needsPhoto && <span className="text-[10px] font-mono uppercase tracking-wider text-[#ff4500] font-bold" data-testid={`item-${idx}-photo-missing`}>Photo missing</span>}
            </div>
            <PhotoUploader photoIds={item.photo_ids || []} onChange={(ids) => !readOnly && onUpdate({ photo_ids: ids })} testIdPrefix={`item-${idx}`} />
          </div>

          {/* Trigger Action — checkbox + conditional fields, link audit → corrective action */}
          <TriggerActionBlock idx={idx} item={item} onUpdate={onUpdate} readOnly={readOnly} runId={runId} draft={draft} onDraftChange={onDraftChange} />
        </div>
      </div>
    </div>
  );
}

function TriggerActionBlock({ idx, item, onUpdate, readOnly, runId, draft, onDraftChange }) {
  const on = !!item.trigger_action;
  const [people, setPeople] = useState([]);
  const [teams, setTeams] = useState([]);
  const [newTag, setNewTag] = useState("");
  // Local buffered state — commits to backend only on blur, killing the per-keystroke PATCH lag.
  // Hydrated from draft (localStorage) if a resumed session had unsaved text.
  const [details, setDetails] = useState(draft?.action_details ?? item.action_details ?? "");
  const [dueLocal, setDueLocal] = useState(draft?.action_due_date ?? item.action_due_date ?? "");
  const [titleLocal, setTitleLocal] = useState(draft?.action_title ?? item.action_title ?? "");
  useEffect(() => { setDetails(draft?.action_details ?? item.action_details ?? ""); }, [item.action_details]);
  useEffect(() => { setDueLocal(draft?.action_due_date ?? item.action_due_date ?? ""); }, [item.action_due_date]);
  useEffect(() => { setTitleLocal(draft?.action_title ?? item.action_title ?? ""); }, [item.action_title]);
  useEffect(() => {
    if (!on) return;
    api.get("/users/visible-to-me").then((r) => setPeople(r.data || [])).catch(() => {});
    api.get("/teams").then((r) => setTeams(r.data || [])).catch(() => {});
  }, [on]);
  const toggle = () => onUpdate({ trigger_action: !on });
  const toggleId = (key, id) => {
    const arr = item[key] || [];
    onUpdate({ [key]: arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id] });
  };
  const stash = (field, v, serverVal) => {
    if ((v ?? "") !== (serverVal ?? "")) setDraftField(runId, idx, field, v);
    else clearDraftField(runId, idx, field);
    onDraftChange && onDraftChange();
  };
  const onDetails = (v) => { setDetails(v); stash("action_details", v, item.action_details); };
  const onDue = (v) => { setDueLocal(v); stash("action_due_date", v, item.action_due_date); };
  const onTitle = (v) => { setTitleLocal(v); stash("action_title", v, item.action_title); };
  const commitDetails = () => { if (details !== (item.action_details || "")) onUpdate({ action_details: details }); };
  const commitDue = () => { if (dueLocal !== (item.action_due_date || "")) onUpdate({ action_due_date: dueLocal }); };
  const commitTitle = () => { if (titleLocal !== (item.action_title || "")) onUpdate({ action_title: titleLocal }); };
  return (
    <div className="mt-3 border-t border-[#1a1a1a]/08 pt-3" data-testid={`item-${idx}-trigger-block`}>
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" checked={on} disabled={readOnly} onChange={toggle}
          data-testid={`item-${idx}-trigger-checkbox`}
          className="w-4 h-4 accent-[#ff4500]" />
        <span className="text-[12px] font-display font-bold">Trigger action</span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
          spawns a corrective Action on close-out
        </span>
      </label>
      {on && (
        <div className="mt-2 bg-[#fff1ea] border border-[#ff4500]/20 rounded-xl p-3 space-y-3" data-testid={`item-${idx}-trigger-fields`}>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Action title <span className="text-[#ff4500]">*</span> <span className="normal-case text-[#1a1a1a]/45">(60 chars — e.g. &quot;Repair belt on Machine 7&quot;)</span></label>
            <input data-testid={`item-${idx}-action-title`}
              value={titleLocal} maxLength={60} disabled={readOnly}
              onChange={(e) => onTitle(e.target.value)}
              onBlur={commitTitle}
              placeholder="Short, action-oriented title"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
            <div className="text-right text-[10px] font-mono text-[#1a1a1a]/40">{titleLocal.length}/60</div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Action details</label>
            <textarea data-testid={`item-${idx}-action-details`}
              value={details} disabled={readOnly}
              onChange={(e) => onDetails(e.target.value)}
              onBlur={commitDetails}
              placeholder="What corrective work is needed?"
              className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm h-16" />
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/45 mt-1">saves when you click away · no lag</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Assign to people</label>
              <div className="flex flex-wrap gap-1.5">
                {people.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">No people visible.</span>}
                {people.map((u) => {
                  const sel = (item.action_assignee_user_ids || []).includes(u.id);
                  return (
                    <button key={u.id} type="button" disabled={readOnly}
                      data-testid={`item-${idx}-action-assign-user-${u.id}`}
                      onClick={() => toggleId("action_assignee_user_ids", u.id)}
                      className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${sel ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Assign to hubs</label>
              <div className="flex flex-wrap gap-1.5">
                {teams.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">No hubs.</span>}
                {teams.map((t) => {
                  const sel = (item.action_assignee_team_ids || []).includes(t.id);
                  return (
                    <button key={t.id} type="button" disabled={readOnly}
                      data-testid={`item-${idx}-action-assign-team-${t.id}`}
                      onClick={() => toggleId("action_assignee_team_ids", t.id)}
                      className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${sel ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#ff4500] border-[#ff4500]/30"}`}>
                      {t.name}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Due date</label>
              <input type="date" data-testid={`item-${idx}-action-due`}
                value={dueLocal} disabled={readOnly}
                onChange={(e) => onDue(e.target.value)}
                onBlur={commitDue}
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">Tags</label>
              <input type="text" data-testid={`item-${idx}-action-tag-input`}
                value={newTag} disabled={readOnly}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && newTag.trim()) { e.preventDefault(); onUpdate({ action_tags: [...(item.action_tags || []), newTag.trim()] }); setNewTag(""); } }}
                placeholder="press enter to add"
                className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm" />
              <div className="flex flex-wrap gap-1 mt-1.5">
                {(item.action_tags || []).map((t, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-white border border-[#ff4500]/30 text-[#ff4500] text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                    {t}
                    {!readOnly && <button onClick={() => onUpdate({ action_tags: (item.action_tags || []).filter((_, j) => j !== i) })} className="hover:opacity-80">×</button>}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// --- Per-kind inputs ---------------------------------------------------

function PassFail({ idx, item, onUpdate, readOnly }) {
  return (
    <div className="flex flex-wrap gap-2">
      <PFBtn idx={idx} value="pass" icon={Check} label="Pass" color="#1a8754" current={item.status} disabled={readOnly} onClick={() => onUpdate({ status: "pass" })} />
      <PFBtn idx={idx} value="fail" icon={X} label="Fail" color="#ff4500" current={item.status} disabled={readOnly} onClick={() => onUpdate({ status: "fail" })} />
      <PFBtn idx={idx} value="na" icon={CircleSlash} label="N/A" color="#1a1a1a" current={item.status} disabled={readOnly} onClick={() => onUpdate({ status: "na" })} />
    </div>
  );
}

function PFBtn({ idx, value, icon: Icon, label, color, current, disabled, onClick }) {
  const on = current === value;
  return (
    <button data-testid={`item-${idx}-${value}`} disabled={disabled} onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold border rounded-md transition-colors duration-150 ${
        on ? "text-white border-transparent" : "border-[#1a1a1a]/30 text-[#1a1a1a] hover:border-[#1a1a1a]"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
      style={on ? { background: color } : {}}>
      <Icon className="w-3 h-3" /> {label}
    </button>
  );
}

function Written({ idx, item, onUpdate, readOnly, runId, draft, onDraftChange }) {
  const [val, setVal] = useState(draft?.answer_text ?? item.answer_text ?? "");
  useEffect(() => { setVal(draft?.answer_text ?? item.answer_text ?? ""); }, [item.answer_text]);
  const onChange = (v) => {
    setVal(v);
    if ((v ?? "") !== (item.answer_text ?? "")) setDraftField(runId, idx, "answer_text", v);
    else clearDraftField(runId, idx, "answer_text");
    onDraftChange && onDraftChange();
  };
  const save = () => { if (val !== item.answer_text) onUpdate({ answer_text: val }); };
  const hasDraft = "answer_text" in (draft || {});
  return (
    <div>
      <textarea data-testid={`item-${idx}-written`} value={val} disabled={readOnly}
        onChange={(e) => onChange(e.target.value)} onBlur={save}
        placeholder="Type your answer…"
        className="w-full bg-white border border-[#1a1a1a]/30 px-2 py-1.5 text-sm h-24 rounded-md" />
      {hasDraft && (
        <div className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[#a37e00] inline-flex items-center gap-1" data-testid={`item-${idx}-written-draft-badge`}>
          <Save className="w-2.5 h-2.5" /> draft · click away to save
        </div>
      )}
    </div>
  );
}

function MultipleChoice({ idx, item, onUpdate, readOnly }) {
  const opts = (item.options || []).filter((o) => o && o.trim());
  const multi = !!item.multi_select;
  const selected = item.answer_choices || [];
  const toggle = (o) => {
    if (readOnly) return;
    if (multi) {
      const next = selected.includes(o) ? selected.filter((x) => x !== o) : [...selected, o];
      onUpdate({ answer_choices: next });
    } else {
      onUpdate({ answer_choices: [o] });
    }
  };
  return (
    <div className="space-y-1.5">
      {opts.length === 0 ? (
        <div className="text-[11px] text-[#1a1a1a]/55 font-mono uppercase tracking-wider">No options configured.</div>
      ) : opts.map((o, oi) => {
        const on = selected.includes(o);
        return (
          <button key={oi} type="button" disabled={readOnly}
            data-testid={`item-${idx}-choice-${oi}`} onClick={() => toggle(o)}
            className={`w-full text-left flex items-center gap-2 border rounded-md px-3 py-2 text-sm transition-colors duration-150 ${
              on ? "bg-[#1a1a1a] text-white border-[#1a1a1a]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15 hover:border-[#1a1a1a]/40"
            } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}>
            <span className={`w-4 h-4 flex items-center justify-center text-[10px] font-bold ${multi ? "rounded-sm" : "rounded-full"} border ${on ? "bg-[#ff4500] border-[#ff4500] text-white" : "border-current"}`}>
              {on ? (multi ? "✓" : "●") : ""}
            </span>
            <span className="flex-1">{o}</span>
          </button>
        );
      })}
      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">{multi ? "Select any that apply" : "Pick one"}</div>
    </div>
  );
}

function Rank({ idx, item, onUpdate, readOnly }) {
  const scale = Math.max(3, Math.min(10, item.scale || 5));
  const current = item.answer_rank || 0;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {Array.from({ length: scale }, (_, n) => {
        const val = n + 1;
        const on = current === val;
        return (
          <button key={val} type="button" disabled={readOnly}
            data-testid={`item-${idx}-rank-${val}`}
            onClick={() => onUpdate({ answer_rank: val })}
            className={`w-9 h-9 rounded-full font-display font-black text-sm border transition-colors duration-150 ${
              on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20 hover:border-[#1a1a1a]"
            } ${readOnly ? "opacity-60 cursor-not-allowed" : ""}`}>
            {val}
          </button>
        );
      })}
      <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 ml-1">1 = low · {scale} = high</span>
    </div>
  );
}
